// Pure decision helpers for the put-wall "Confirm Release" flow.
//
// These are the server-state-derived building blocks for the SkuBin sweep that
// opens release modals. They live outside the component so they can be unit
// tested (see putRelease.test.ts) and so the sweep's candidate source is a
// single, auditable function rather than in-memory session state.
//
// Guiding principle (see
// Docs/Internal/operator-ui-orchestrator-restart-resilience-plan.md §2):
// server state is the source of truth. The release-modal candidates must be
// reconstructable purely from the polled `/PickStation/putTasks` response, so
// that an orchestrator restart or page refresh can never strand a pending put
// task with no UI path to release it.

/**
 * WMS truth for "all picks for this order are done".
 *
 * Accepts either casing of the flag (`ArePicksDone` / `arePicksDone`) and both
 * boolean and stringified-boolean values ("true"/"True"). Anything else —
 * `false`, `undefined`, a missing order — is treated as not-done so the sweep
 * never opens a premature release modal.
 */
export const areOrderPicksDone = (order: any): boolean => {
  const value = order?.ArePicksDone ?? order?.arePicksDone;
  return value === true || String(value).toLowerCase() === "true";
};

/**
 * Release-modal candidate order refs derived from server state.
 *
 * Given the polled put tasks and the component's `isActivePutTask` predicate,
 * returns the trimmed, de-duplicated `putTask.orderReferenceId` of every task
 * that is still active. Tasks with a missing/blank order ref are dropped
 * (defensive — order-tote-mapping tasks come from a different endpoint, but a
 * malformed put task must never produce an empty-ref candidate).
 */
export const getReleaseCandidateOrderRefs = (
  putTasks: any[],
  isActivePutTask: (task: any) => boolean
): string[] => {
  if (!Array.isArray(putTasks)) {
    return [];
  }

  const seen = new Set<string>();
  const candidates: string[] = [];

  for (const task of putTasks) {
    if (!isActivePutTask(task)) {
      continue;
    }

    const orderReferenceId = String(task?.putTask?.orderReferenceId ?? "").trim();
    if (!orderReferenceId || seen.has(orderReferenceId)) {
      continue;
    }

    seen.add(orderReferenceId);
    candidates.push(orderReferenceId);
  }

  return candidates;
};
